import { styled } from 'styled-components';

export const StickyHeaderShell = styled.div<{ $isCompact?: boolean }>`
  ${({ $isCompact, theme }) =>
    $isCompact &&
    `
    position: sticky;
    top: 0;
    z-index: 100;
    background: ${theme.colors.background};
  `}
`;

export const HeaderContainer = styled.div<{ $isCompact?: boolean }>`
  display: ${({ $isCompact }) => ($isCompact ? 'block' : 'flex')};
  gap: ${({ $isCompact }) => ($isCompact ? '0' : '1rem')};
  padding: ${({ $isCompact }) => ($isCompact ? '0.65rem 1.25rem' : '1rem 2rem')};
  align-items: ${({ $isCompact }) => ($isCompact ? 'stretch' : 'center')};
  flex-wrap: ${({ $isCompact }) => ($isCompact ? 'nowrap' : 'nowrap')};
  border-bottom: ${({ theme, $isCompact }) =>
    $isCompact
      ? `1px solid ${theme.colors.border.light}`
      : `2px solid ${theme.colors.text.primary}`};
  background-color: ${({ theme }) => theme.colors.background};
`;

export const CompactHeaderStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  width: 100%;
`;

export const CompactHeaderMainRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  width: 100%;

  @media (max-width: 900px) {
    flex-wrap: wrap;
  }
`;

export const BreadcrumbNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.72rem;
  line-height: 1.3;
  min-width: 0;
`;

export const BreadcrumbLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

export const BreadcrumbText = styled.span<{ $isCurrent?: boolean }>`
  color: ${({ theme, $isCurrent }) =>
    $isCurrent ? theme.colors.text.primary : theme.colors.text.secondary};
  font-weight: ${({ $isCurrent }) => ($isCurrent ? 600 : 400)};
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const BreadcrumbSep = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.65;
  user-select: none;
`;

export const PageContextRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.5rem;
  min-width: 0;
`;

export const ContextEyebrow = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ContextSubtitle = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  width: 100%;
`;

export const WorkspaceTypeBadge = styled.span`
  flex-shrink: 0;
  padding: 0.14rem 0.45rem;
  font-size: 0.68rem;
  font-weight: 600;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => `${theme.colors.primary}10`};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

export const CompactQuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.3rem;
`;

export const CompactQuickActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: 999px;
  padding: 0.22rem 0.5rem;
  font-size: 0.68rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}08`};
    border-color: ${({ theme }) => `${theme.colors.primary}28`};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const HeaderMetaDetails = styled.details`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
`;

export const HeaderMetaChevron = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform 0.2s ease;

  details[open] & {
    transform: rotate(180deg);
  }
`;

export const HeaderMetaSummary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.38rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  list-style: none;
  user-select: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}06`};
  }
`;

export const HeaderMetaSummaryText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderMetaBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.5rem 0.45rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`;

export const RightSectionStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    align-items: flex-start;
  }
`;

export const LeftSection = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isCompact }) => ($isCompact ? '0.5rem' : '1rem')};
  flex-shrink: 0;
`;

export const CenterSection = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: ${({ $isCompact }) => ($isCompact ? '0.1rem' : '0')};
`;

export const RightSection = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isCompact }) => ($isCompact ? '0.5rem' : '1rem')};
  flex-shrink: 0;
`;

export const WorkspaceDataContainer = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isCompact }) => ($isCompact ? '0.15rem' : '0')};
  min-width: 0;
  width: 100%;
`;

export const CompactWorkspaceTitle = styled.h1`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const CompactNavButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.35rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}08`};
    border-color: ${({ theme }) => `${theme.colors.primary}28`};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const AskSessionDataContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AskSessionInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.125rem;
  max-width: 260px;
`;

export const AskSessionLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.secondary || '#6b7280'};
`;

export const AskSessionTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text || '#1f2937'};
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
`;

export const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const Description = styled.p<{ $isCompact?: boolean }>`
  margin: 0;
  font-size: ${({ $isCompact }) => ($isCompact ? '0.8rem' : '14px')};
  line-height: ${({ $isCompact }) => ($isCompact ? '1.4' : '1.5')};
  color: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.text.secondary : '#6b7280'};
  ${({ $isCompact }) =>
    $isCompact &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 100%;
  `}
`;

export const ClientMatterDetails = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: ${({ $isCompact }) => ($isCompact ? 'row' : 'row')};
  align-items: ${({ $isCompact }) => ($isCompact ? 'center' : 'flex-start')};
  gap: ${({ $isCompact }) => ($isCompact ? '0.5rem' : '1.5rem')};
  flex-wrap: wrap;
`;

export const DetailBlock = styled.div<{ $isCompact?: boolean }>`
  display: ${({ $isCompact }) => ($isCompact ? 'inline' : 'flex')};
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
`;

export const DetailName = styled.span<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.75rem' : '0.9rem')};
  font-weight: ${({ $isCompact }) => ($isCompact ? '500' : '500')};
  color: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.text.secondary : theme.colors.text || '#1f2937'};
  line-height: 1.25;
`;

export const DetailNumber = styled.span<{ $isCompact?: boolean }>`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.25;

  ${({ $isCompact }) => $isCompact && 'display: none;'}
`;

export const ExtraDetails = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ $isCompact }) => ($isCompact ? '0.35rem' : '1rem')};
  flex-wrap: wrap;
`;

export const MemberCount = styled.span<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.72rem' : '0.875rem')};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CompactInviteButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.3rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}08`};
    border-color: ${({ theme }) => `${theme.colors.primary}28`};
  }
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0;
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text || '#333'};
`;

export const EditButtonStyled = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary || '#0078d4'};
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.25rem 0.5rem;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

export const DisplayValue = styled.div`
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.colors.background || '#f5f5f5'};
  border-radius: 4px;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
`;

export const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
`;

export const MemberTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.background || '#f5f5f5'};
  border-radius: 4px;
  gap: 1rem;
`;

export const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.25rem;
`;

export const MemberName = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text || '#333'};
`;

export const MemberEmail = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.secondary || '#666'};
`;

export const MemberActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const AdminToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const AdminCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

export const AdminLabel = styled.label`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text || '#333'};
  cursor: pointer;
  user-select: none;
`;

export const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error || '#d32f2f'}15;
  }
`;

export const EditableTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

export const EditableDescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

export const EditableTitleInput = styled.input<{ $isCompact?: boolean }>`
  margin: 0;
  font-size: ${({ $isCompact }) => ($isCompact ? '1.05rem' : '1.5rem')};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ $isCompact }) => ($isCompact ? '0.2rem 0.45rem' : '0.25rem 0.5rem')};
  background: ${({ theme }) => theme.colors.background};
  flex: 1;
  min-width: 0;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.primary}20`};
  }
`;

export const EditableDescriptionInput = styled.textarea`
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
  border: 1px solid #3b82f6;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  background: white;
  flex: 1;
  outline: none;
  resize: vertical;
  min-height: 2rem;
  font-family: inherit;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

export const SaveButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #059669;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

export const CancelButton = styled.button`
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #4b5563;
  }
`;

export const InlineEditButton = styled.button<{ $isCompact?: boolean }>`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: ${({ $isCompact }) => ($isCompact ? '0.15rem' : '0.25rem')};
  margin-left: ${({ $isCompact }) => ($isCompact ? '0.25rem' : '0.5rem')};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isCompact }) => ($isCompact ? 0.55 : 0)};
  flex-shrink: 0;
  transition: opacity 0.2s, color 0.2s;

  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  &:hover ${InlineEditButton} {
    opacity: 0.7;
  }

  &:hover ${InlineEditButton}:hover {
    opacity: 1;
  }
`;

export const DescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  &:hover ${InlineEditButton} {
    opacity: 0.7;
  }

  &:hover ${InlineEditButton}:hover {
    opacity: 1;
  }
`;

export const AddMemberButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary || '#0078d4'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  align-self: flex-start;

  &:hover {
    opacity: 0.9;
  }
`;
